import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "count", "validation", "selectAll", "machineLabel", "machineText"]

  connect() {
    this.updateCount()
    this.validateMachineCount()
  }

  // Toggle all checkboxes
  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((checkbox, index) => {
      checkbox.checked = checked
      this.updateMachineStyle(checkbox, this.machineLabelTargets[index], this.machineTextTargets[index])
    })
    this.updateCount()
    this.validateMachineCount()
  }

  // Update selected machine count
  updateCount() {
    if (this.hasCountTarget) {
      const selectedCount = this.selectedMachines().length
      this.countTarget.textContent = selectedCount

      // Remove all color classes
      this.countTarget.parentElement.classList.remove(
        'bg-indigo-50', 'text-indigo-700', 'ring-indigo-600/20',
        'dark:bg-indigo-900/20', 'dark:text-indigo-400',
        'bg-gray-50', 'text-gray-700', 'ring-gray-600/20',
        'dark:bg-gray-900/20', 'dark:text-gray-400',
        'bg-yellow-50', 'text-yellow-700', 'ring-yellow-600/20',
        'dark:bg-yellow-900/20', 'dark:text-yellow-400',
        'bg-green-50', 'text-green-700', 'ring-green-600/20',
        'dark:bg-green-900/20', 'dark:text-green-400'
      )

      // Apply color based on count: Green for 6, Yellow for <6 or >6
      if (selectedCount === 6) {
        this.countTarget.parentElement.classList.add(
          'bg-green-50', 'text-green-700', 'ring-green-600/20',
          'dark:bg-green-900/20', 'dark:text-green-400'
        )
      } else {
        this.countTarget.parentElement.classList.add(
          'bg-yellow-50', 'text-yellow-700', 'ring-yellow-600/20',
          'dark:bg-yellow-900/20', 'dark:text-yellow-400'
        )
      }

      // Animate count change
      this.countTarget.classList.add("scale-110", "font-bold")
      setTimeout(() => {
        this.countTarget.classList.remove("scale-110", "font-bold")
      }, 300)
    }
  }

  // Validate that we have enough machines
  validateMachineCount() {
    const selectedCount = this.selectedMachines().length

    if (!this.hasValidationTarget) return

    if (selectedCount === 0) {
      this.showValidation("No machines selected", "error")
    } else {
      const gamesPerTeam = this.getGamesPerTeam(selectedCount)
      this.showValidation(
        `Each team plays ${gamesPerTeam} ${gamesPerTeam === 1 ? 'game' : 'games'}`,
        "success"
      )
    }
  }

  // Calculate games per team based on selected machines
  getGamesPerTeam(selectedCount) {
    // If games_per_team input exists, use that value
    const gamesPerTeamInput = document.getElementById('games_per_team')
    if (gamesPerTeamInput) {
      const value = parseInt(gamesPerTeamInput.value)
      return !isNaN(value) ? Math.min(value, selectedCount) : Math.min(5, selectedCount)
    }

    // Default to 5 or machine count, whichever is smaller
    return Math.min(5, selectedCount)
  }

  showValidation(message, type) {
    this.validationTarget.textContent = message

    // Reset classes
    this.validationTarget.classList.remove(
      "text-red-600", "dark:text-red-400",
      "text-yellow-600", "dark:text-yellow-400",
      "text-green-600", "dark:text-green-400"
    )

    // Apply type-specific classes
    if (type === "error") {
      this.validationTarget.classList.add("text-red-600", "dark:text-red-400")
    } else if (type === "warning") {
      this.validationTarget.classList.add("text-yellow-600", "dark:text-yellow-400")
    } else if (type === "success") {
      this.validationTarget.classList.add("text-green-600", "dark:text-green-400")
    }
  }

  // Called when checkbox changes
  checkboxChanged(event) {
    const checkbox = event.target
    const index = this.checkboxTargets.indexOf(checkbox)
    this.updateMachineStyle(checkbox, this.machineLabelTargets[index], this.machineTextTargets[index])

    this.updateCount()
    this.validateMachineCount()

    // Update "select all" checkbox state
    if (this.hasSelectAllTarget) {
      const allChecked = this.checkboxTargets.every(cb => cb.checked)
      const noneChecked = this.checkboxTargets.every(cb => !cb.checked)
      this.selectAllTarget.checked = allChecked
      this.selectAllTarget.indeterminate = !allChecked && !noneChecked
    }
  }

  // Update the visual styling of a machine based on its checked state
  updateMachineStyle(checkbox, label, text) {
    const selectedLabelClasses = ['bg-indigo-50', 'dark:bg-indigo-950', 'border-indigo-300', 'dark:border-indigo-700', 'hover:bg-indigo-100', 'dark:hover:bg-indigo-900']
    const unselectedLabelClasses = ['border-gray-200', 'dark:border-gray-700', 'hover:bg-gray-50', 'dark:hover:bg-gray-800']
    const selectedTextClasses = ['text-indigo-900', 'dark:text-indigo-100', 'font-medium']
    const unselectedTextClasses = ['text-gray-700', 'dark:text-gray-300']

    if (checkbox.checked) {
      label.classList.remove(...unselectedLabelClasses)
      label.classList.add(...selectedLabelClasses)
      text.classList.remove(...unselectedTextClasses)
      text.classList.add(...selectedTextClasses)
    } else {
      label.classList.remove(...selectedLabelClasses)
      label.classList.add(...unselectedLabelClasses)
      text.classList.remove(...selectedTextClasses)
      text.classList.add(...unselectedTextClasses)
    }
  }

  selectedMachines() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }
}
